/**
 * PlayerProfile - Public profile page showing user's portfolio with applied cosmetic skins.
 * Accessible at /player/:userId
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Trophy, TrendingUp, TrendingDown, Calendar, Star, Shield, Crown,
  Flame, Rocket, Diamond, Moon, Sun, Fish, Zap, Sparkles, Eye, Lock, EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map badge icon names to lucide components
const BADGE_ICONS = {
  diamond: Diamond, flame: Flame, rocket: Rocket, moon: Moon,
  sunrise: Sun, whale: Fish, lotus: Sparkles, shark: Fish, phoenix: Flame,
  crown: Crown, lightning: Zap, star: Star, shield: Shield, trophy: Trophy,
};

// Card style CSS generator
function getCardStyles(cardStyle) {
  if (!cardStyle?.preview_data) return {};
  const p = cardStyle.preview_data;
  const styles = {};
  if (p.image_url) {
    styles.backgroundImage = `linear-gradient(rgba(15,23,42,0.85), rgba(15,23,42,0.85)), url(${p.image_url})`;
    styles.backgroundSize = 'cover';
    styles.backgroundPosition = 'center';
  } else if (p.background) {
    styles.background = p.background;
  }
  if (p.backdrop_blur) styles.backdropFilter = `blur(${p.backdrop_blur})`;
  if (p.border) styles.border = p.border;
  if (p.box_shadow) styles.boxShadow = p.box_shadow;
  return styles;
}

// Avatar frame renderer
function AvatarFrame({ frame, username, size = 80 }) {
  const letter = (username || '?')[0].toUpperCase();
  const p = frame?.preview_data || {};
  const borderStyle = p.border || '3px solid #4b5563';
  const isAnimated = p.animated;
  const hasCrown = p.crown;
  const hasGlow = p.glow;
  const effect = p.effect;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {hasCrown && (
        <Crown
          className="absolute -top-4 left-1/2 -translate-x-1/2 text-yellow-400 drop-shadow-lg"
          style={{ filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.6))' }}
          size={24}
        />
      )}
      {/* Frame artwork ring behind the avatar */}
      {p.image_url && (
        <img
          src={p.image_url}
          alt="frame"
          className="absolute inset-0 w-full h-full rounded-full object-cover"
          style={{ transform: 'scale(1.25)', opacity: 0.8 }}
        />
      )}
      <div
        className={`relative rounded-full flex items-center justify-center text-white font-bold ${isAnimated ? 'animate-pulse' : ''}`}
        style={{
          width: size, height: size,
          border: borderStyle,
          fontSize: size * 0.4,
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          boxShadow: hasGlow ? `0 0 20px ${p.border?.match(/#[a-f0-9]+/i)?.[0] || '#ffd700'}40` : undefined,
          ...(effect === 'fire' ? { boxShadow: '0 0 20px rgba(249,115,22,0.6), 0 0 40px rgba(249,115,22,0.3)' } : {}),
          ...(effect === 'lightning' ? { boxShadow: `0 0 20px ${p.color || '#facc15'}80, 0 0 40px ${p.color || '#facc15'}40` } : {}),
          ...(effect === 'galaxy' ? { boxShadow: '0 0 25px rgba(139,92,246,0.5), 0 0 50px rgba(59,130,246,0.3)' } : {}),
        }}
        data-testid="avatar-frame"
      >
        {letter}
      </div>
    </div>
  );
}

// Badge renderer
function PlayerBadge({ badge }) {
  if (!badge?.preview_data) return null;
  const p = badge.preview_data;
  const Icon = BADGE_ICONS[p.icon] || Star;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${p.animated ? 'animate-pulse' : ''}`}
      style={{
        background: `${p.color}20`,
        color: p.color,
        border: `1px solid ${p.color}40`,
        boxShadow: p.glow ? `0 0 12px ${p.color}40` : undefined,
      }}
      title={badge.name}
      data-testid="player-badge"
    >
      {p.image_url ? (
        <img src={p.image_url} alt={badge.name} className="w-5 h-5 rounded-full object-cover" />
      ) : (
        <Icon size={14} />
      )}
      {badge.name}
    </div>
  );
}

export default function PlayerProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerAchievements, setPlayerAchievements] = useState([]);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${API_URL}/api/profile/${userId}`, { headers });
        if (!res.ok) throw new Error('Player not found');
        setProfile(await res.json());
        // Fetch player's unlocked achievements
        const achRes = await fetch(`${API_URL}/api/achievements/player/${userId}`);
        if (achRes.ok) {
          const achData = await achRes.json();
          setPlayerAchievements(achData.achievements || []);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [userId, token]);

  // Theme colors
  const theme = useMemo(() => {
    const t = profile?.cosmetics?.theme?.preview_data;
    return t || { primary: '#10b981', secondary: '#6ee7b7', background: '#0f172a', accent: '#34d399', card_bg: 'rgba(16,185,129,0.08)' };
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0f172a' }}>
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#0f172a' }}>
        <p className="text-slate-400">{error || 'Player not found'}</p>
        <Button onClick={() => navigate(-1)} variant="outline"><ArrowLeft className="h-4 w-4 mr-2" /> Go Back</Button>
      </div>
    );
  }

  const { cosmetics, portfolio, stats } = profile;
  const cardStyle = cosmetics?.card_style;
  const badge = cosmetics?.badge;
  const frame = cosmetics?.avatar_frame;
  const isPrivate = portfolio?.is_private;

  // Portfolio calculations
  const totalGainPct = portfolio.total_value > 0
    ? ((portfolio.total_value - 100000) / 100000 * 100)
    : 0;

  return (
    <div
      className="min-h-screen pb-24 transition-colors duration-500"
      style={{ background: theme.background }}
      data-testid="player-profile-page"
    >
      {/* Theme artwork as hero background */}
      {theme.image_url ? (
        <div
          className="absolute inset-x-0 top-0 h-72 pointer-events-none"
          style={{
            backgroundImage: `url(${theme.image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        />
      ) : (
        <div
          className="absolute inset-x-0 top-0 h-72 opacity-30 pointer-events-none"
          style={{ background: `linear-gradient(180deg, ${theme.primary}40 0%, transparent 100%)` }}
        />
      )}

      <div className="relative max-w-2xl mx-auto px-4 pt-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 text-slate-400 hover:text-white"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        {/* Profile header */}
        <div className="flex items-center gap-5 mb-8">
          <AvatarFrame frame={frame} username={profile.username} size={80} />
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-white">{profile.username}</h1>
              {profile.is_pro && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">PRO</Badge>
              )}
              {badge && <PlayerBadge badge={badge} />}
            </div>
            <p className="text-sm mt-1" style={{ color: `${theme.primary}cc` }}>
              {stats.global_rank ? `Rank #${stats.global_rank}` : 'Unranked'}
              {stats.weeks_played > 0 && ` | ${stats.weeks_played} weeks played`}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div
          className="grid grid-cols-3 gap-3 mb-8"
        >
          {[
            { label: 'Portfolio Value', value: `V$${portfolio.total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, sub: `${totalGainPct >= 0 ? '+' : ''}${totalGainPct.toFixed(1)}%`, positive: totalGainPct >= 0 },
            { label: 'Total Points', value: stats.total_points.toLocaleString(), sub: `Avg ${stats.average_points}/wk` },
            { label: 'Best Week', value: stats.best_week.toLocaleString(), sub: 'pts' },
          ].map((stat) => (
            <Card
              key={stat.label}
              className="p-3 border-0"
              style={{
                background: theme.card_bg || `${theme.primary}10`,
                border: `1px solid ${theme.primary}20`,
                ...getCardStyles(cardStyle),
              }}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <p className="text-xs text-slate-400 mb-1">{stat.label}</p>
              <p className="text-lg font-bold text-white">{stat.value}</p>
              <p className={`text-xs ${stat.positive === true ? 'text-emerald-400' : stat.positive === false ? 'text-red-400' : 'text-slate-500'}`}>
                {stat.sub}
              </p>
            </Card>
          ))}
        </div>

        {/* Holdings */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              Holdings ({portfolio.holdings_count}/10)
              {isPrivate && <Lock size={14} className="text-slate-500" />}
            </h2>
            {!isPrivate && portfolio.cash_balance != null && (
              <span className="text-xs px-2 py-1 rounded-full" style={{ background: `${theme.primary}20`, color: theme.primary }}>
                V${portfolio.cash_balance.toLocaleString(undefined, { maximumFractionDigits: 0 })} cash
              </span>
            )}
          </div>

          {isPrivate ? (
            /* Private portfolio - blurred placeholder */
            <div className="relative" data-testid="private-portfolio-overlay">
              {/* Fake blurred holdings for visual effect */}
              <div className="space-y-2 select-none" style={{ filter: 'blur(8px)', opacity: 0.4, pointerEvents: 'none' }}>
                {[1, 2, 3, 4].map(i => (
                  <Card key={i} className="p-4 border-0" style={{ background: theme.card_bg || `${theme.primary}08`, border: `1px solid ${theme.primary}15` }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-700" />
                        <div>
                          <div className="h-4 w-16 bg-slate-600 rounded" />
                          <div className="h-3 w-24 bg-slate-700 rounded mt-1" />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="h-4 w-20 bg-slate-600 rounded" />
                        <div className="h-3 w-16 bg-slate-700 rounded mt-1" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {/* Overlay message */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="bg-slate-900/90 backdrop-blur-sm px-6 py-4 rounded-xl border border-slate-700 text-center">
                  <EyeOff className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-white font-medium">Private Portfolio</p>
                  <p className="text-slate-400 text-sm mt-1">This player has hidden their holdings</p>
                  <Badge className="mt-2 bg-amber-500/20 text-amber-400 border-amber-500/30">
                    <Crown size={12} className="mr-1" /> Pro Feature
                  </Badge>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {portfolio.holdings.map((h) => {
                const gainLoss = (h.current_price_vd - h.buy_price_vd) * h.units;
                const gainPct = h.buy_price_vd > 0 ? ((h.current_price_vd - h.buy_price_vd) / h.buy_price_vd * 100) : 0;
                const isUp = gainLoss >= 0;

                return (
                  <Card
                    key={h.symbol}
                    className="p-4 border-0 transition-all duration-200 hover:scale-[1.01]"
                    style={{
                      background: theme.card_bg || `${theme.primary}08`,
                      border: `1px solid ${theme.primary}15`,
                      ...getCardStyles(cardStyle),
                    }}
                    data-testid={`holding-${h.symbol}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
                          style={{ background: `${theme.primary}20`, color: theme.primary }}
                        >
                          {h.symbol.slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{h.symbol}</p>
                          <p className="text-xs text-slate-500">{h.units} units @ V${h.buy_price_vd.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-white">
                          V${(h.current_price_vd * h.units).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                        <div className={`flex items-center gap-1 text-xs ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          <span>{isUp ? '+' : ''}{gainPct.toFixed(1)}%</span>
                          <span className="text-slate-500">
                            ({isUp ? '+' : ''}V${gainLoss.toFixed(0)})
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}

              {portfolio.holdings.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No holdings yet</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Medals - All earned achievements */}
        {playerAchievements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Trophy size={18} style={{ color: theme.primary }} /> Medals ({playerAchievements.length})
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {playerAchievements.map(ach => (
                <div
                  key={ach.id}
                  className="group relative rounded-lg overflow-hidden text-center cursor-default"
                  style={{ background: `${theme.primary}10`, border: `1px solid ${theme.primary}20` }}
                  title={`${ach.name}: ${ach.description}`}
                >
                  {ach.image_url ? (
                    <img src={ach.image_url} alt={ach.cosmetic_name} className="w-full h-20 object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-20 flex items-center justify-center" style={{ background: `${theme.primary}15` }}>
                      <Star size={20} style={{ color: theme.primary }} />
                    </div>
                  )}
                  <p className="text-white text-[10px] font-medium px-1 py-1.5 truncate">{ach.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Equipped Cosmetics */}
        {Object.keys(cosmetics).length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-3">Equipped Cosmetics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(cosmetics).map(([slot, item]) => (
                <div
                  key={slot}
                  className="rounded-lg overflow-hidden text-center"
                  style={{ background: `${theme.primary}10`, border: `1px solid ${theme.primary}25` }}
                >
                  {item.preview_data?.image_url ? (
                    <img src={item.preview_data.image_url} alt={item.name} className="w-full h-24 object-cover" />
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center" style={{ background: `${theme.primary}15` }}>
                      <Sparkles size={24} style={{ color: theme.primary }} />
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-white text-xs font-medium truncate">{item.name}</p>
                    <p className="text-slate-500 text-[10px] capitalize">{slot.replace('_', ' ')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
